import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../components/Global/Loader';
import { MdDeleteForever } from "react-icons/md";
import { FaEdit } from 'react-icons/fa';

function Consultants() {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Add this line
  const [consultantToDelete, setConsultantToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/consultant/all/details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      // console.log('API Response:', response.data.data);
      setConsultants(response.data.data);
    } catch (error) {
      // console.error('Error fetching consultants:', error);
      setError(error.response?.data?.message || 'Failed to fetch consultants');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/consultant/${consultantToDelete._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setShowDeleteModal(false);
      setConsultantToDelete(null);
      fetchConsultants(); // Refresh the list
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete consultant");
    } finally {
      setLoading(false);

    }
  };

  const handleDelete = (consultant) => {
    setConsultantToDelete(consultant);
    setShowDeleteModal(true);
  };

  const handleEdit = (id) => {
    navigate(`/admin/consultants/add?id=${id}`);
  };

  // Add this filtering function before the return statement
  const filteredConsultants = consultants.filter((consultant) => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      consultant.consultantId?.toLowerCase().includes(searchTerm) ||
      consultant.firstName?.toLowerCase().includes(searchTerm) ||
      consultant.lastName?.toLowerCase().includes(searchTerm) ||
      consultant.email?.toLowerCase().includes(searchTerm) ||
      consultant.cellPhone?.toLowerCase().includes(searchTerm) ||
      consultant.campusCity?.toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div id="main-content" className="relative min-h-screen pl-36 mt-16 w-full p-8">
      {loading && <Loader />}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[30%]">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the consultant record for{' '}
              <span className="font-semibold">
                {consultantToDelete?.firstName} {consultantToDelete?.lastName}
              </span>
              ? This action cannot be undone!
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConsultantToDelete(null);
                }}
                className="px-4 py-2 text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Consultants</h1>
          <div className="flex items-center space-x-4"> {/* Added flex container */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search consultants..."
                className="py-2 px-3 w-64 text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 transition duration-300 shadow-sm"
                style={{ borderBottomWidth: "3px", borderBottomColor: "#3b82f6" }}
              />
              <i className="fas fa-search absolute right-3 top-3 text-gray-400"></i>
            </div>
            <button
              className="px-4 py-2 font-semibold text-white rounded bg-blue-500 hover:bg-blue-600 flex items-center"
              onClick={() => navigate('/admin/consultants/add')}
            >
              <i className="fas fa-plus mr-2"></i>
              Add Consultant
            </button>
          </div>
        </div>
        <table className="w-full table-auto border-t border-b border-gray-300">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="px-4 py-3 text-gray-400"></th>
              <th className="px-4 py-3 text-gray-400">Consultant ID</th>
              <th className="px-4 py-3 text-gray-400">Name</th>
              <th className="px-4 py-3 text-gray-400">Email</th>
              <th className="px-4 py-3 text-gray-400">Phone</th>
              <th className="px-4 py-3 text-gray-400">City</th>
              <th className="px-4 py-3 text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredConsultants) && filteredConsultants.length > 0 ? (
              filteredConsultants.map((consultant) => (
                <tr
                  key={consultant._id}
                  className="border-b border-gray-300 text-center hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <img
                      src={consultant?.profileURL ? consultant.profileURL : "/Assets/default_user.png"}
                      alt="User Profile"
                      className="w-12 h-12 rounded-full object-cover border border-gray-200 mx-auto"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{consultant.consultantId || "N/A"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <span>{consultant.firstName + " " + consultant.lastName}</span>
                    {consultant.hasSalesPersonRole && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        CRM Access
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{consultant.email || "N/A"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{consultant.cellPhone || "N/A"}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{consultant.campusCity || "N/A"}</td>
                  <td className="px-4 pt-6 flex items-center justify-center space-x-3">
                    <FaEdit
                      className="text-blue-500 cursor-pointer hover:text-blue-600"
                      onClick={() => handleEdit(consultant._id)}
                      size={20}
                    />
                    <MdDeleteForever
                      className="text-red-500 cursor-pointer hover:text-red-600"
                      onClick={() => handleDelete(consultant)}
                      size={22}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-sm text-gray-400 mt-1">No data available</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>


        {error && (
          <div className="text-center text-red-500 mt-4">
            {error}
          </div>
        )}

        {!loading && (!consultants || consultants.length === 0) && (
          <div className="text-center text-gray-500 mt-4">
            No consultants found
          </div>
        )}
      </div>
    </div>
  );
}

export default Consultants;